// biome-ignore lint/style/noRestrictedImports: intentional
import { matchRoutes, RouteObject } from "react-router-dom";
import { validate as validateUUID } from "uuid";

import type { ResourceInteractionType } from "@hightouch/lib/resource-interactions/resource-interaction-types";

export const marketingPaths = ["/audiences/:id/*", "/journeys/:id/*"];
export const activationPaths = ["/syncs/:sync_id/*"];
// Resources surfaced only on the agentic home's "Recently viewed" rail.
// Kept separate from the pre-existing marketing/activation buckets
// to avoid blank cards in the legacy home.
export const studioPaths = [
  "/models/:model_id/*",
  "/analytics/:id/*",
  "/dashboards/:dashboardId/*",
  "/experiments/:experimentId/*",
  "/lifecycle-studio/projects/:campaignId/*",
  "/ad-studio/projects/:projectId/*",
  "/agents/reports/:id/*",
];

// A recently-viewed resource entry.
export type Meta = {
  resource: ResourceInteractionType;
  id: string;
  // ISO timestamp recorded when the resource was last viewed. Absent on entries
  // persisted before recency tracking existed; those sort oldest.
  viewedAt?: string;
};

// Simple route patterns for matching without importing components
const routePatterns: RouteObject[] = [
  { path: "/audiences/:id/*" },
  { path: "/journeys/:id/*" },
  { path: "/syncs/:sync_id/*" },
  { path: "/models/:model_id/*" },
  { path: "/analytics/:id/*" },
  { path: "/dashboards/:dashboardId/*" },
  { path: "/experiments/:experimentId/*" },
  { path: "/lifecycle-studio/projects/:campaignId/*" },
  { path: "/ad-studio/projects/:projectId/*" },
  { path: "/agents/reports/:id/*" },
];

export const matchCurrentRoute = (pathname: string) => {
  // strip the workspace from the pathname
  const strippedPathname = "/" + pathname.split("/").slice(2).join("/");
  return matchRoutes(routePatterns, strippedPathname);
};

const idMeta = (
  resource: Meta["resource"],
  id: string | undefined,
): Meta | null => (id ? { resource, id } : null);

const uuidMeta = (
  resource: Meta["resource"],
  id: string | undefined,
): Meta | null => (id && validateUUID(id) ? { resource, id } : null);

type RouteParams = Record<string, string | undefined>;

// Parse relevant id from path
export const parseRouteToMeta = (
  path: string,
  params: RouteParams,
): Meta | null => {
  switch (path) {
    case "/audiences/:id/*":
      return idMeta("audiences", params.id);
    case "/journeys/:id/*":
      return uuidMeta("journeys", params.id);
    case "/syncs/:sync_id/*":
      return idMeta("syncs", params.sync_id);
    case "/models/:model_id/*":
      return idMeta("models", params.model_id);
    case "/analytics/:id/*":
      return uuidMeta("charts", params.id);
    case "/dashboards/:dashboardId/*":
      return uuidMeta("dashboards", params.dashboardId);
    case "/experiments/:experimentId/*":
      return uuidMeta("experiments", params.experimentId);
    case "/lifecycle-studio/projects/:campaignId/*":
      return uuidMeta("campaigns", params.campaignId);
    case "/ad-studio/projects/:projectId/*":
      return uuidMeta("ad-studio-projects", params.projectId);
    case "/agents/reports/:id/*":
      return uuidMeta("reports", params.id);
    default:
      return null;
  }
};
